import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import StatusBadge from '../../components/common/StatusBadge';
import { getFitTests } from '../../api/fitTests';
import { getEmpresas } from '../../api/empresas';
import { useAuth } from '../../contexts/AuthContext';
import { tipoIcon } from '../../utils/fitTestOpcoes';

const DIAS_PT  = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MESES_PT = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const SITUACOES_AGENDA = ['em_analise', 'confirmado', 'standby'];
const COL_W = 190; // px por coluna de dia

function isoDate(d) { return d.toISOString().slice(0, 10); }
function fmtData(d) { return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`; }

function semanaDeData(ref) {
  const d = new Date(ref); d.setHours(0,0,0,0);
  const dom = new Date(d); dom.setDate(d.getDate() - d.getDay());
  const sab = new Date(dom); sab.setDate(dom.getDate() + 6);
  return { dom, sab };
}

function contarPorTipo(tests) {
  const map = {};
  tests.forEach(t => {
    const k = `${t.tipo_teste}/${t.grupo_teste||''}`;
    if (!map[k]) map[k] = { tipo: t.tipo_teste, grupo: t.grupo_teste, count: 0 };
    map[k].count++;
  });
  return Object.values(map);
}

// ─── Coluna de um dia ─────────────────────────────────────────────────────────
function DiaColuna({ dia, tests, isFenix, navigate }) {
  const ehHoje  = isoDate(dia) === isoDate(new Date());
  const temTeste = tests.length > 0;
  const dataKey  = isoDate(dia);

  // Fenix: agrupa por empresa
  const porEmpresa = useMemo(() => {
    if (!isFenix) return [];
    const map = {};
    tests.forEach(t => {
      const id   = t.funcionario_empresa?.empresa?.id_empresa || 'sem';
      const nome = t.funcionario_empresa?.empresa?.nome_fantasia
                || t.funcionario_empresa?.empresa?.razao_social || 'Sem empresa';
      if (!map[id]) map[id] = { id, nome, tests: [] };
      map[id].tests.push(t);
    });
    return Object.values(map).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [tests, isFenix]);

  const headerBg = ehHoje ? 'var(--main-color)' : '#f8f9fa';
  const headerCo = ehHoje ? '#fff' : '#495057';

  return (
    <div style={{ flex: `0 0 ${COL_W}px`, width: COL_W }}>
      <div className="border rounded h-100 d-flex flex-column" style={{ minHeight: 300 }}>
        {/* Cabeçalho do dia */}
        <div className="px-2 py-2 d-flex justify-content-between align-items-center rounded-top"
          style={{ background: headerBg, color: headerCo }}>
          <div>
            <div className="fw-bold small">{DIAS_PT[dia.getDay()]}, {fmtData(dia)}</div>
          </div>
          <span className="badge rounded-pill"
            style={{ background: temTeste ? (ehHoje ? '#fff3' : 'var(--main-color)') : '#e9ecef',
                     color: temTeste ? '#fff' : '#6c757d', fontSize: '.7rem' }}>
            {tests.length}
          </span>
        </div>

        {/* Conteúdo */}
        <div className="p-1 flex-fill overflow-auto" style={{ maxHeight: 420 }}>
          {tests.length === 0 ? (
            <div className="text-center text-muted py-3" style={{ fontSize: '.75rem', opacity: .5 }}>
              <i className="bi bi-calendar-x d-block fs-5 mb-1"></i>Livre
            </div>
          ) : isFenix ? (
            // ── Visão Fenix: card por empresa ─────────────────────────────
            porEmpresa.map(emp => (
              <div key={emp.id}
                className="border rounded p-2 mb-1"
                style={{ cursor: 'pointer', background: '#f8f9ff', transition: 'box-shadow .15s' }}
                onClick={() => navigate('/agenda/detalhe', { state: { data: dataKey, empresa: emp } })}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,.12)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}>
                <div className="fw-bold" style={{ fontSize: '.8rem', color: 'var(--main-color)' }}>
                  {emp.nome}
                </div>
                <div className="text-muted mt-1 d-flex flex-wrap gap-1">
                  {contarPorTipo(emp.tests).map(({ tipo, grupo, count }) => (
                    <span key={`${tipo}${grupo}`}
                      className="badge bg-secondary"
                      style={{ fontSize: '.65rem' }}>
                      <i className={`bi ${tipoIcon(tipo)} me-1`}></i>
                      {grupo || tipo} ×{count}
                    </span>
                  ))}
                </div>
                <div className="text-muted mt-1" style={{ fontSize: '.7rem' }}>
                  {emp.tests.length} teste(s) · ver detalhes →
                </div>
              </div>
            ))
          ) : (
            // ── Visão Cliente: card por teste ──────────────────────────────
            tests.map(t => (
              <div key={t.id_fit_test}
                className="border rounded p-2 mb-1"
                style={{ background: '#fafafa', fontSize: '.78rem' }}>
                <div className="fw-500 text-truncate">
                  <i className={`bi ${tipoIcon(t.tipo_teste)} me-1 text-muted`}></i>
                  {t.funcionario_empresa?.funcionario?.nome || '—'}
                </div>
                {t.local?.nome && (
                  <div className="text-muted text-truncate">
                    <i className="bi bi-geo-alt me-1"></i>{t.local.nome}
                  </div>
                )}
                <div className="mt-1">
                  <StatusBadge status={t.situacao} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
const FILTROS_SITUACAO = [
  { value: '',           label: 'Todos' },
  { value: 'agendados',  label: 'Agendados (a realizar)' },
  { value: 'realizados', label: 'Realizados' },
  { value: 'analise',    label: 'Em análise' },
  { value: 'standby',    label: 'Standby' },
];

export default function Agenda() {
  const { isFenix } = useAuth();
  const navigate = useNavigate();
  const [tests, setTests] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refDate, setRefDate] = useState(new Date());
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroSituacao, setFiltroSituacao] = useState('');

  useEffect(() => {
    const reqs = [getFitTests()];
    if (isFenix) reqs.push(getEmpresas());
    Promise.all(reqs).then(([t, e]) => {
      setTests(t);
      if (e) setEmpresas(e);
      setLoading(false);
    });
  }, [isFenix]);

  const { dom, sab } = semanaDeData(refDate);

  const dias = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(dom); d.setDate(dom.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, [dom]);

  const testsByDay = useMemo(() => {
    const ini = isoDate(dom);
    const fim = isoDate(sab);

    let filtrados = tests.filter(t =>
      t.data_agendada >= ini && t.data_agendada <= fim &&
      (!filtroEmpresa || t.funcionario_empresa?.empresa?.id_empresa === filtroEmpresa)
    );

    if (filtroSituacao === 'agendados') {
      filtrados = filtrados.filter(t => t.situacao === 'confirmado' && !t.data_realizada);
    } else if (filtroSituacao === 'realizados') {
      filtrados = filtrados.filter(t => t.situacao === 'confirmado' && !!t.data_realizada);
    } else if (filtroSituacao === 'analise') {
      filtrados = filtrados.filter(t => t.situacao === 'em_analise');
    } else if (filtroSituacao === 'standby') {
      filtrados = filtrados.filter(t => t.situacao === 'standby');
    } else {
      filtrados = filtrados.filter(t => SITUACOES_AGENDA.includes(t.situacao));
    }

    const map = {};
    dias.forEach(d => { map[isoDate(d)] = []; });
    filtrados.forEach(t => { if (map[t.data_agendada]) map[t.data_agendada].push(t); });
    return map;
  }, [tests, dom, sab, filtroEmpresa, filtroSituacao, dias]);

  const totalSemana = Object.values(testsByDay).reduce((s, v) => s + v.length, 0);

  if (loading) return <LoadingSpinner />;

  return (
    <>
      <PageHeader title="Agenda" subtitle="Testes agendados por semana" />

      {/* Barra de navegação */}
      <div className="card-datafit p-3 mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <button className="btn btn-sm btn-outline-secondary" onClick={() => { const d = new Date(refDate); d.setDate(d.getDate()-7); setRefDate(d); }}>
          <i className="bi bi-chevron-left me-1"></i>Anterior
        </button>

        <div className="text-center">
          <div className="fw-bold" style={{ color: 'var(--main-color)' }}>
            {fmtData(dom)} a {fmtData(sab)} — {MESES_PT[sab.getMonth()]} {sab.getFullYear()}
          </div>
          <div className="small text-muted">{totalSemana} teste(s) nesta semana</div>
        </div>

        <div className="d-flex gap-2 align-items-center flex-wrap">
          {/* Filtro de situação — disponível para todos */}
          <select className="form-select form-select-sm" style={{ width: 190 }}
            value={filtroSituacao} onChange={e => setFiltroSituacao(e.target.value)}>
            {FILTROS_SITUACAO.map(f => (
              <option key={f.value} value={f.value}>{f.label}</option>
            ))}
          </select>

          {/* Filtro de empresa — só fenix */}
          {isFenix && empresas.length > 0 && (
            <select className="form-select form-select-sm" style={{ width: 200 }}
              value={filtroEmpresa} onChange={e => setFiltroEmpresa(e.target.value)}>
              <option value="">Todas as empresas</option>
              {empresas.map(e => <option key={e.id_empresa} value={e.id_empresa}>{e.nome_fantasia || e.razao_social}</option>)}
            </select>
          )}

          <button className="btn btn-sm btn-outline-secondary" onClick={() => setRefDate(new Date())}>Hoje</button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => { const d = new Date(refDate); d.setDate(d.getDate()+7); setRefDate(d); }}>
            Próxima<i className="bi bi-chevron-right ms-1"></i>
          </button>
        </div>
      </div>

      {/* Grade de dias — scroll horizontal, sempre lado a lado */}
      <div style={{ overflowX: 'auto', overflowY: 'visible', paddingBottom: 8 }}>
        <div style={{ display: 'flex', gap: 8, minWidth: dias.length * (COL_W + 8) }}>
          {dias.map(dia => (
            <DiaColuna
              key={isoDate(dia)}
              dia={dia}
              tests={testsByDay[isoDate(dia)] || []}
              isFenix={isFenix}
              navigate={navigate}
            />
          ))}
        </div>
      </div>

      {/* Legenda */}
      <div className="d-flex gap-3 mt-3 small text-muted flex-wrap align-items-center">
        <span><StatusBadge status="em_analise" /> Em análise</span>
        <span><StatusBadge status="confirmado" /> Confirmado / A realizar</span>
        <span><StatusBadge status="standby" /> Standby</span>
        <span>
          <i className="bi bi-check-circle-fill me-1" style={{ color: '#198754' }}></i>
          Realizado (com resultado)
        </span>
        {isFenix && <span className="fst-italic ms-2">· Clique em uma empresa para ver os testes</span>}
      </div>
    </>
  );
}
